import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter,
  AlertDialogHeader, AlertDialogOverlay, Box, Button, useDisclosure, useToast,
} from "@chakra-ui/react";
import { SideFilter, type FilterField } from "../../../components/SideFilter";
import { AdminTabSwitch } from "../../../components/AdminTabSwitch";
import CandidateDetailDrawer from "../onboarding/CandidateDetailDrawer.tsx";
import type { Candidate } from "../../../types/onboarding.ts";
import { getToken, getUser } from "../../../lib/auth.ts";
import { useApiRequest } from "../../../hooks/useApiRequest.ts";
import { useInfiniteSectionScroll } from "../../../hooks/useInfiniteSectionScroll.ts";
import { useSectionPage } from "../../../hooks/useSectionPage.ts";
import { useDownloadManager } from "../../../contexts/DownloadManagerContext";
import { settings } from "../../../shared/settings.ts";
import { getHrAuditCandidates, getHrFilters, getHrProcessDashboard, getHrProcessStats, type HrFilterOptions, type HrProcessStats } from "../../../lib/api.ts";
import type { WhitelistEntry } from "../../../types/whitelist.ts";
import { HrProcessesTab } from "../onboarding/components/HrProcessesTab";
import type { KanbanColumnData } from "../onboarding/components/KanbanColumn";
import type { SectionGroup } from "../onboarding/components/CandidateSectionAccordion";
import type { ViewMode } from "../onboarding/components/ViewToggle";
import { HrAuditLogPage } from "../onboarding/components/HrAuditLogPage";
import { usePermissionsContext } from "../../../data/permissions/permissionContext";
import { PERMISSIONS } from "../../../config/permissions";
import { getCurrentWeekRange, toYmd } from "../../../lib/weekRange";
import { WeekFilterChip } from "../onboarding/components/WeekFilterChip";

// Tamanho de página por coluna do Kanban — grande o bastante pra preencher a
// altura visível da coluna (senão o scroll infinito dispararia a próxima
// página em sequência e acabaria carregando tudo de uma vez de qualquer forma).
const HR_KANBAN_PAGE_SIZE = 10;
// Tamanho de página por seção na visualização em lista (accordion).
const HR_LIST_PAGE_SIZE = 3;

interface HrProcessSideFilters {
  recruiter: string[];
  manager: string[];
  cost_center: string[];
  status: "" | "aguardando_rh" | "em_andamento" | "concluido" | "cancelado";
  concluded_at: { startDate: Date; endDate: Date; key: string } | null;
  admission_date: { startDate: Date; endDate: Date; key: string } | null;
  dp_finished: "" | "true" | "false";
}

const DEFAULT_HR_FILTERS: HrProcessSideFilters = {
  recruiter: [],
  manager: [],
  cost_center: [],
  status: "",
  concluded_at: null,
  admission_date: null,
  dp_finished: "",
};

const HR_PROCESS_STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "",              label: "Todos" },
  { value: "aguardando_rh", label: "Não iniciado" },
  { value: "em_andamento",  label: "Em andamento" },
  { value: "concluido",     label: "Concluído" },
  { value: "cancelado",     label: "Cancelado" },
];

/** Valor do filtro de status → chave da coluna/seção do Kanban/lista.
 * O filtro usa o valor do banco (concluido, singular) enquanto as colunas/seções
 * usam "concluidos" (plural, herança da view antiga) — mapear para casar. */
const HR_STATUS_TO_SECTION_KEY: Record<string, string> = {
  aguardando_rh: "aguardando_rh",
  em_andamento: "em_andamento",
  concluido: "concluidos",
  cancelado: "cancelados",
};

interface HrAuditSideFilters {
  search: string;
  date: { startDate: Date; endDate: Date; key: string } | null;
  candidate: string[];
}

const DEFAULT_AUDIT_FILTERS: HrAuditSideFilters = {
  search: "",
  date: null,
  candidate: [],
};

function whitelistEntryToCandidate(e: WhitelistEntry): Candidate {
  const pct = e.total_steps > 0 ? Math.round((e.completed_steps / e.total_steps) * 100) : 0;
  return {
    user_id: e.id,
    name: e.candidate_full_name ?? e.email,
    email: e.email,
    position: e.job_title ?? "",
    department: e.job_department ?? "",
    completed_missions: e.completed_steps,
    total_missions: e.total_steps,
    progress_pct: pct,
    status: e.hr_process_status.toUpperCase() as Candidate["status"],
    is_pj: e.is_pj ?? null,
    responsible_manager: e.responsible_manager,
    admission_exam_completed: e.admission_exam_completed,
  };
}

/** Busca uma página de UM status de processo RH — cada coluna do Kanban
 * pagina e busca de forma independente das demais (ver useInfiniteSectionScroll). */
async function fetchHrSectionPage(
  token: string,
  search: string | undefined,
  filters: HrProcessSideFilters & { concluded_from?: string; concluded_to?: string; cancelled_from?: string; cancelled_to?: string; admission_from?: string; admission_to?: string },
  status: string,
  page: number,
  pageSize: number,
) {
  if (!token) return { entries: [] as WhitelistEntry[], candidates: [] as Candidate[], total: 0, total_pages: 1 };
  const dp_finished = filters.dp_finished === "true" ? true : filters.dp_finished === "false" ? false : undefined;
  const res = await getHrProcessDashboard(token, search, page, pageSize, {
    ...filters,
    status,
    dp_finished,
  });
  return { entries: res.entries, candidates: res.entries.map(whitelistEntryToCandidate), total: res.total, total_pages: res.total_pages };
}

const HrProcessesPage = () => {
  const { permissions } = usePermissionsContext();
  const hasHistory = permissions.includes(PERMISSIONS.RH.HISTORY_VIEW);

  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>("kanban");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [hrFilters, setHrFilters] = useState<HrProcessSideFilters>({ ...DEFAULT_HR_FILTERS });
  // Filtro "Apenas esta semana" aplicado por padrão às seções Concluídos e Cancelados.
  const [concluidoWeekOnly, setConcluidoWeekOnly] = useState(true);
  const [canceladoWeekOnly, setCanceladoWeekOnly] = useState(true);
  const weekRange = useMemo(() => getCurrentWeekRange(), []);
  const [hrFilterOptions, setHrFilterOptions] = useState<HrFilterOptions | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);

  const [hrStats, setHrStats] = useState<HrProcessStats | null>(null);
  const [hrWhitelistMap, setHrWhitelistMap] = useState<Record<string, WhitelistEntry>>({});
  // Seleção de candidatos para Carta Proposta (idêntico ao "Baixar Documentos").
  const [selectedHrCandidateIds, setSelectedHrCandidateIds] = useState<Set<string | number>>(new Set());

  const [auditFilters, setAuditFilters] = useState<HrAuditSideFilters>({ ...DEFAULT_AUDIT_FILTERS });
  const [auditCandidateOptions, setAuditCandidateOptions] = useState<{ value: string; label: string }[]>([]);
  const [auditRefreshKey, setAuditRefreshKey] = useState(0);

  const auditDateFrom = auditFilters.date?.startDate
    ? new Date(auditFilters.date.startDate).toISOString().slice(0, 10)
    : undefined;
  const auditDateTo = auditFilters.date?.endDate
    ? new Date(auditFilters.date.endDate).toISOString().slice(0, 10)
    : undefined;

  // Data de conclusão do onboarding (mission_maps.completed_at no starter-pack) —
  // mesmo formato YYYY-MM-DD usado no filtro de período da aba de auditoria.
  const hrConcludedFrom = hrFilters.concluded_at?.startDate
    ? new Date(hrFilters.concluded_at.startDate).toISOString().slice(0, 10)
    : undefined;
  const hrConcludedTo = hrFilters.concluded_at?.endDate
    ? new Date(hrFilters.concluded_at.endDate).toISOString().slice(0, 10)
    : undefined;

  // Data de admissão (hr_processes.admission_date no gg-hub) — YYYY-MM-DD.
  const hrAdmissionFrom = hrFilters.admission_date?.startDate
    ? new Date(hrFilters.admission_date.startDate).toISOString().slice(0, 10)
    : undefined;
  const hrAdmissionTo = hrFilters.admission_date?.endDate
    ? new Date(hrFilters.admission_date.endDate).toISOString().slice(0, 10)
    : undefined;

  const [pendingCancelCandidate, setPendingCancelCandidate] = useState<Candidate | null>(null);
  const { isOpen: isCancelConfirmOpen, onOpen: openCancelConfirm, onClose: closeCancelConfirm } = useDisclosure();
  const cancelConfirmRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const { startDownload } = useDownloadManager();

  const token = getToken() ?? "";

  // ── Seleção de candidatos (Carta Proposta) ─────────────────────────────────
  // Mesmo padrão do OnboardingDashboardPage (Baixar Documentos).
  const handleHrToggleSelect = useCallback((id: string | number) => {
    setSelectedHrCandidateIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleHrClearSelection = useCallback(() => setSelectedHrCandidateIds(new Set()), []);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Mapa email → WhitelistEntry para calcular progresso das etapas RH nos
  // cards — mesclado a partir das 3 seções (cada uma busca seu próprio status).
  const mergeHrWhitelistEntries = useCallback((entries: WhitelistEntry[]) => {
    if (!entries.length) return;
    setHrWhitelistMap((prev) => {
      const next = { ...prev };
      entries.forEach((e) => { next[e.email] = e; });
      return next;
    });
  }, []);

  // Filtro de conclusão aplicado APENAS à seção Concluídos: por padrão a semana
  // atual (chip ligado); quando o chip está desligado, usa o filtro manual de
  // "Data de conclusão" do SideFilter (se houver). As demais seções não recebem
  // filtro de conclusão (não têm completed_at — esvaziariam a coluna).
  const concluidoConcludedFrom = concluidoWeekOnly ? toYmd(weekRange.startDate) : hrConcludedFrom;
  const concluidoConcludedTo = concluidoWeekOnly ? toYmd(weekRange.endDate) : hrConcludedTo;

  // Filtro de cancelamento aplicado APENAS à seção Cancelados, mesmo padrão
  // do filtro de conclusão acima. Por padrão = semana atual (chip ligado).
  const canceladoCancelledFrom = canceladoWeekOnly ? toYmd(weekRange.startDate) : undefined;
  const canceladoCancelledTo = canceladoWeekOnly ? toYmd(weekRange.endDate) : undefined;

  const fetchAguardandoRhBase = useCallback(async (page: number, pageSize: number) => {
    const { entries, candidates, total, total_pages } =
      await fetchHrSectionPage(token, debouncedSearch || undefined, { ...hrFilters, admission_from: hrAdmissionFrom, admission_to: hrAdmissionTo }, "aguardando_rh", page, pageSize);
    mergeHrWhitelistEntries(entries);
    return { candidates, total, total_pages };
  }, [token, debouncedSearch, hrFilters, hrAdmissionFrom, hrAdmissionTo, mergeHrWhitelistEntries]);

  const fetchEmAndamentoBase = useCallback(async (page: number, pageSize: number) => {
    const { entries, candidates, total, total_pages } =
      await fetchHrSectionPage(token, debouncedSearch || undefined, { ...hrFilters, admission_from: hrAdmissionFrom, admission_to: hrAdmissionTo }, "em_andamento", page, pageSize);
    mergeHrWhitelistEntries(entries);
    return { candidates, total, total_pages };
  }, [token, debouncedSearch, hrFilters, hrAdmissionFrom, hrAdmissionTo, mergeHrWhitelistEntries]);

  const fetchConcluidosBase = useCallback(async (page: number, pageSize: number) => {
    const { entries, candidates, total, total_pages } =
      await fetchHrSectionPage(token, debouncedSearch || undefined, { ...hrFilters, concluded_from: concluidoConcludedFrom, concluded_to: concluidoConcludedTo, admission_from: hrAdmissionFrom, admission_to: hrAdmissionTo }, "concluido", page, pageSize);
    mergeHrWhitelistEntries(entries);
    return { candidates, total, total_pages };
  }, [token, debouncedSearch, hrFilters, concluidoConcludedFrom, concluidoConcludedTo, hrAdmissionFrom, hrAdmissionTo, mergeHrWhitelistEntries]);

  const fetchCanceladosBase = useCallback(async (page: number, pageSize: number) => {
    const { entries, candidates, total, total_pages } =
      await fetchHrSectionPage(token, debouncedSearch || undefined, { ...hrFilters, cancelled_from: canceladoCancelledFrom, cancelled_to: canceladoCancelledTo, admission_from: hrAdmissionFrom, admission_to: hrAdmissionTo }, "cancelado", page, pageSize);
    mergeHrWhitelistEntries(entries);
    return { candidates, total, total_pages };
  }, [token, debouncedSearch, hrFilters, canceladoCancelledFrom, canceladoCancelledTo, hrAdmissionFrom, hrAdmissionTo, mergeHrWhitelistEntries]);

  const fetchAguardandoRhKanban = useCallback((page: number) => fetchAguardandoRhBase(page, HR_KANBAN_PAGE_SIZE), [fetchAguardandoRhBase]);
  const fetchEmAndamentoKanban  = useCallback((page: number) => fetchEmAndamentoBase(page, HR_KANBAN_PAGE_SIZE),  [fetchEmAndamentoBase]);
  const fetchConcluidosKanban   = useCallback((page: number) => fetchConcluidosBase(page, HR_KANBAN_PAGE_SIZE),   [fetchConcluidosBase]);
  const fetchCanceladosKanban   = useCallback((page: number) => fetchCanceladosBase(page, HR_KANBAN_PAGE_SIZE),   [fetchCanceladosBase]);

  const fetchAguardandoRhList = useCallback((page: number) => fetchAguardandoRhBase(page, HR_LIST_PAGE_SIZE), [fetchAguardandoRhBase]);
  const fetchEmAndamentoList  = useCallback((page: number) => fetchEmAndamentoBase(page, HR_LIST_PAGE_SIZE),  [fetchEmAndamentoBase]);
  const fetchConcluidosList   = useCallback((page: number) => fetchConcluidosBase(page, HR_LIST_PAGE_SIZE),   [fetchConcluidosBase]);
  const fetchCanceladosList   = useCallback((page: number) => fetchCanceladosBase(page, HR_LIST_PAGE_SIZE),   [fetchCanceladosBase]);

  // Kanban: as 4 colunas ficam visíveis de uma vez, então todas buscam a 1ª
  // página ao montar — cada uma pagina depois via scroll infinito próprio.
  const hrTabEnabled = activeTab === 0;
  const kanbanEnabled = hrTabEnabled && viewMode === "kanban";
  const aguardandoRhKanban = useInfiniteSectionScroll(fetchAguardandoRhKanban, kanbanEnabled);
  const emAndamentoKanban  = useInfiniteSectionScroll(fetchEmAndamentoKanban,  kanbanEnabled);
  const concluidosKanban   = useInfiniteSectionScroll(fetchConcluidosKanban,   kanbanEnabled);
  const canceladosKanban   = useInfiniteSectionScroll(fetchCanceladosKanban,   kanbanEnabled);

  // Lista (accordion): só "Não Iniciado" vem aberto/buscado ao carregar a
  // tela — as demais seções só disparam sua requisição quando o usuário
  // expande o accordion delas.
  const [activatedHrSections, setActivatedHrSections] = useState<Set<string>>(() => new Set(["aguardando_rh"]));
  const handleHrSectionExpand = useCallback((key: string) => {
    setActivatedHrSections((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  }, []);
  const listEnabled = hrTabEnabled && viewMode === "lista";
  const aguardandoRhList = useSectionPage(fetchAguardandoRhList, [debouncedSearch, hrFilters], listEnabled && activatedHrSections.has("aguardando_rh"));
  const emAndamentoList  = useSectionPage(fetchEmAndamentoList,  [debouncedSearch, hrFilters], listEnabled && activatedHrSections.has("em_andamento"));
  const concluidosList   = useSectionPage(fetchConcluidosList,   [debouncedSearch, hrFilters, concluidoWeekOnly], listEnabled && activatedHrSections.has("concluidos"));
  const canceladosList   = useSectionPage(fetchCanceladosList,   [debouncedSearch, hrFilters, canceladoWeekOnly], listEnabled && activatedHrSections.has("cancelados"));

  const handleHrSelectAll = useCallback(() => {
    const allCandidates = viewMode === "kanban"
      ? [...aguardandoRhKanban.candidates, ...emAndamentoKanban.candidates, ...concluidosKanban.candidates, ...canceladosKanban.candidates]
      : [...aguardandoRhList.candidates, ...emAndamentoList.candidates, ...concluidosList.candidates, ...canceladosList.candidates];
    if (selectedHrCandidateIds.size === allCandidates.length && allCandidates.length > 0) {
      setSelectedHrCandidateIds(new Set());
    } else {
      setSelectedHrCandidateIds(new Set(allCandidates.map((c) => c.user_id)));
    }
  }, [viewMode, aguardandoRhKanban, emAndamentoKanban, concluidosKanban, canceladosKanban, aguardandoRhList, emAndamentoList, concluidosList, canceladosList, selectedHrCandidateIds.size]);

  const handleExportCartaProposta = useCallback(async () => {
    if (selectedHrCandidateIds.size === 0) {
      toast({ title: "Nenhum candidato selecionado", description: "Selecione pelo menos 1 candidato.", status: "warning", duration: 4000, isClosable: true });
      return;
    }
    const allCandidates = viewMode === "kanban"
      ? [...aguardandoRhKanban.candidates, ...emAndamentoKanban.candidates, ...concluidosKanban.candidates, ...canceladosKanban.candidates]
      : [...aguardandoRhList.candidates, ...emAndamentoList.candidates, ...concluidosList.candidates, ...canceladosList.candidates];
    const selectedEmails = allCandidates
      .filter((c) => selectedHrCandidateIds.has(c.user_id))
      .map((c) => c.email);
    if (selectedEmails.length === 0) {
      toast({ title: "Nenhum candidato encontrado", description: "Não foi possível obter os emails.", status: "warning", duration: 4000, isClosable: true });
      return;
    }
    try {
      await startDownload(
        { emails: selectedEmails, requested_by: getUser()?.email ?? "" },
        `Carta Proposta (${selectedEmails.length} selecionado${selectedEmails.length !== 1 ? "s" : ""})`,
        settings.environment.VITE_STARTER_PACK_BASE_URL ?? "",
        "onboarding/candidates/export/carta-proposta/jobs",
      );
      handleHrClearSelection();
    } catch {
      toast({ title: "Erro ao gerar Carta Proposta", description: "Não foi possível iniciar a geração.", status: "error", duration: 8000, isClosable: true });
    }
  }, [selectedHrCandidateIds, viewMode, aguardandoRhKanban, emAndamentoKanban, concluidosKanban, canceladosKanban, aguardandoRhList, emAndamentoList, concluidosList, canceladosList, startDownload, toast, handleHrClearSelection]);

  const hrError = viewMode === "kanban"
    ? aguardandoRhKanban.error || emAndamentoKanban.error || concluidosKanban.error || canceladosKanban.error
    : aguardandoRhList.error || emAndamentoList.error || concluidosList.error || canceladosList.error;

  const retryHrSections = useCallback(() => {
    aguardandoRhKanban.refetch(); emAndamentoKanban.refetch(); concluidosKanban.refetch(); canceladosKanban.refetch();
    aguardandoRhList.refetch(); emAndamentoList.refetch(); concluidosList.refetch(); canceladosList.refetch();
  }, [aguardandoRhKanban, emAndamentoKanban, concluidosKanban, canceladosKanban, aguardandoRhList, emAndamentoList, concluidosList, canceladosList]);

  const fetchHrFilters = useCallback(() => {
    if (!token) return;
    getHrFilters(token).then(setHrFilterOptions).catch(() => {});
  }, [token]);

  const fetchAuditCandidates = useCallback(() => {
    if (!token) return;
    getHrAuditCandidates(token)
      .then((rows) => setAuditCandidateOptions(
        rows.map((c) => ({ value: c.email, label: c.name || c.email })),
      ))
      .catch(() => {});
  }, [token]);

  // Big numbers: rota separada e independente das seções — sempre sobre
  // TODOS os processos RH salvos, sem busca/filtros, e sem recarregar quando
  // uma seção é expandida/buscada.
  const [isLoadingHrStats, setIsLoadingHrStats] = useState(false);
  const fetchHrStats = useCallback(async () => {
    if (!token) return;
    setIsLoadingHrStats(true);
    try {
      setHrStats(await getHrProcessStats(token));
    } catch { /* mantém stale */ }
    finally {
      setIsLoadingHrStats(false);
    }
  }, [token]);

  /** Refresh silencioso da aba RH: atualiza as 3 seções (cada uma na sua
   * página atual), os big numbers e o feed de auditoria (via refreshKey),
   * sem loading. */
  const silentRefreshHr = useCallback(() => {
    aguardandoRhKanban.refetch(true);
    emAndamentoKanban.refetch(true);
    concluidosKanban.refetch(true);
    canceladosKanban.refetch(true);
    aguardandoRhList.refetch(true);
    emAndamentoList.refetch(true);
    concluidosList.refetch(true);
    canceladosList.refetch(true);
    void fetchHrStats();
    setAuditRefreshKey((k) => k + 1);
  }, [
    aguardandoRhKanban, emAndamentoKanban, concluidosKanban, canceladosKanban,
    aguardandoRhList, emAndamentoList, concluidosList, canceladosList, fetchHrStats,
  ]);

  /** Atualização otimista de um campo do WhitelistEntry no mapa da aba RH
   * (faz o progresso do card refletir imediatamente, sem refetch da dashboard). */
  const patchHrWhitelistEntryOptimistic = useCallback(
    (email: string, patch: Partial<WhitelistEntry>) => {
      setHrWhitelistMap((prev) => {
        const current = prev[email];
        if (!current) return prev;
        return { ...prev, [email]: { ...current, ...patch } };
      });
    },
    [],
  );

  useEffect(() => {
    if (activeTab === 0) fetchHrStats();
  }, [activeTab, fetchHrStats]);
  useEffect(() => {
    if (activeTab === 0) fetchHrFilters();
  }, [activeTab, fetchHrFilters]);
  useEffect(() => {
    if (activeTab === 1 && hasHistory) fetchAuditCandidates();
  }, [activeTab, hasHistory, fetchAuditCandidates]);

  const { request: cancelReq } = useApiRequest();

  const handleCancelCandidate = useCallback((candidate: Candidate) => {
    setPendingCancelCandidate(candidate);
    openCancelConfirm();
  }, [openCancelConfirm]);

  const handleConfirmCancel = useCallback(async () => {
    if (!pendingCancelCandidate) return;
    closeCancelConfirm();
    try {
      await cancelReq({
        url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/candidates/${encodeURIComponent(pendingCancelCandidate.email)}/cancel`,
        method: "POST",
        skipToast: true,
      });
      toast({ title: `${pendingCancelCandidate.name} marcado como desistente`, status: "warning", duration: 4000, isClosable: true });
      retryHrSections();
      fetchHrStats();
    } catch {
      toast({ title: "Erro ao cancelar candidato", status: "error", duration: 4000, isClosable: true });
    } finally {
      setPendingCancelCandidate(null);
    }
  }, [pendingCancelCandidate, cancelReq, retryHrSections, fetchHrStats, toast, closeCancelConfirm]);

  const handleReactivateCandidate = useCallback(async (candidate: Candidate) => {
    try {
      await cancelReq({
        url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/candidates/${encodeURIComponent(candidate.email)}/reactivate`,
        method: "POST",
        skipToast: true,
      });
      toast({ title: `${candidate.name} reativado com sucesso`, status: "success", duration: 4000, isClosable: true });
      retryHrSections();
      fetchHrStats();
    } catch {
      toast({ title: "Erro ao reativar candidato", status: "error", duration: 4000, isClosable: true });
    }
  }, [cancelReq, retryHrSections, fetchHrStats, toast]);

  // Exportar Excel: mesma rota do starter-pack usada na tela de Mapa de
  // Candidatos (o back já enriquece a resposta com os dados dos Processos
  // Internos de RH — ver get_export_data no starter-pack). Sem "Baixar
  // Documentos" e sem seleção de candidatos nesta tela.
  const handleExport = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (debouncedSearch) params.search = debouncedSearch;
      // Quem pediu — usado pelo gg-hub para validar a permissão de salário.
      const user = getUser();
      if (user?.email) params.requested_by = user.email;
      await startDownload(
        params,
        "Exportar Excel",
        settings.environment.VITE_STARTER_PACK_BASE_URL ?? "",
        "onboarding/candidates/export/xlsx/jobs",
      );
    } catch {
      toast({ title: "Erro ao iniciar exportação Excel", status: "error", duration: 4000, isClosable: true });
    }
  }, [debouncedSearch, startDownload, toast]);

  const recruiterOptions: { value: string; label: string }[] =
    (hrFilterOptions?.recruiters ?? []).map((r) => ({ value: r.email, label: r.name }));
  const managerOptions: { value: string; label: string }[] =
    (hrFilterOptions?.managers ?? []).map((m) => ({ value: m, label: m }));
  const costCenterOptions: { value: string; label: string }[] =
    (hrFilterOptions?.cost_centers ?? []).map((c) => ({ value: c, label: c }));
  const DP_FINISHED_FILTERS: { value: string; label: string }[] = [
    { value: "",      label: "Todos" },
    { value: "true",  label: "DP finalizado" },
    { value: "false", label: "DP não finalizado" },
  ];

  const hrFilterFields: FilterField[] = [
    { key: "status", label: "Status", type: "select", options: HR_PROCESS_STATUS_FILTERS },
    { key: "dp_finished", label: "DP", type: "select", options: DP_FINISHED_FILTERS },
    { key: "recruiter", label: "Recrutador responsável", type: "multiSelect", options: recruiterOptions },
    { key: "manager", label: "Gestor responsável", type: "multiSelect", options: managerOptions },
    { key: "cost_center", label: "Centro de custo", type: "multiSelect", options: costCenterOptions },
    { key: "admission_date", label: "Data de admissão", type: "dateRange" },
    { key: "concluded_at", label: "Data de conclusão da trilha", type: "dateRange" },
  ];

  const auditFilterFields: FilterField[] = [
    { key: "search", label: "Busca", type: "text", placeholder: "Buscar por candidato, autor ou campo..." },
    { key: "candidate", label: "Candidato", type: "multiSelect", options: auditCandidateOptions },
    { key: "date", label: "Período", type: "dateRange" },
  ];

  // Cada coluna já vem paginada e filtrada por status do próprio back — sem
  // agrupamento client-side de uma página global.
  // Com um status específico no SideFilter, só a coluna/seção correspondente é
  // renderizada (as demais retornariam vazio de qualquer forma).
  const hrColumns: KanbanColumnData[] = [
    {
      key: "aguardando_rh", label: "Não Iniciado", dot: "orange.400",
      candidates: aguardandoRhKanban.candidates, total: aguardandoRhKanban.total,
      hasMore: aguardandoRhKanban.hasMore, onLoadMore: aguardandoRhKanban.loadMore,
      isLoadingInitial: aguardandoRhKanban.isLoadingInitial, isLoadingMore: aguardandoRhKanban.isLoadingMore,
      hasFetched: aguardandoRhKanban.hasFetched,
    },
    {
      key: "em_andamento", label: "Em andamento", dot: "blue.400",
      candidates: emAndamentoKanban.candidates, total: emAndamentoKanban.total,
      hasMore: emAndamentoKanban.hasMore, onLoadMore: emAndamentoKanban.loadMore,
      isLoadingInitial: emAndamentoKanban.isLoadingInitial, isLoadingMore: emAndamentoKanban.isLoadingMore,
      hasFetched: emAndamentoKanban.hasFetched,
    },
    {
      key: "concluidos", label: "Concluídos", dot: "green.400",
      candidates: concluidosKanban.candidates, total: concluidosKanban.total,
      hasMore: concluidosKanban.hasMore, onLoadMore: concluidosKanban.loadMore,
      isLoadingInitial: concluidosKanban.isLoadingInitial, isLoadingMore: concluidosKanban.isLoadingMore,
      hasFetched: concluidosKanban.hasFetched,
      headerExtra: (
        <WeekFilterChip
          active={concluidoWeekOnly}
          weekStart={weekRange.startDate}
          weekEnd={weekRange.endDate}
          onToggle={() => setConcluidoWeekOnly((v) => !v)}
          activeTitle="Mostrar todos os concluídos"
          inactiveTitle="Filtrar apenas os concluídos nesta semana"
        />
      ),
    },
    {
      key: "cancelados", label: "Cancelados", dot: "red.400",
      candidates: canceladosKanban.candidates, total: canceladosKanban.total,
      hasMore: canceladosKanban.hasMore, onLoadMore: canceladosKanban.loadMore,
      isLoadingInitial: canceladosKanban.isLoadingInitial, isLoadingMore: canceladosKanban.isLoadingMore,
      hasFetched: canceladosKanban.hasFetched,
      headerExtra: (
        <WeekFilterChip
          active={canceladoWeekOnly}
          weekStart={weekRange.startDate}
          weekEnd={weekRange.endDate}
          onToggle={() => setCanceladoWeekOnly((v) => !v)}
          label="Cancelado essa semana"
          activeTitle="Mostrar todos os cancelados"
          inactiveTitle="Filtrar apenas os cancelados nesta semana"
        />
      ),
    },
  ].filter((c) => hrFilters.status === "" || c.key === (HR_STATUS_TO_SECTION_KEY[hrFilters.status] ?? hrFilters.status));

  const hrSectionGroups: SectionGroup[] = [
    {
      key: "aguardando_rh", label: "Não Iniciado", dot: "orange.400",
      candidates: aguardandoRhList.candidates,
      page: aguardandoRhList.page, totalPages: aguardandoRhList.totalPages,
      onPageChange: aguardandoRhList.setPage, isLoading: aguardandoRhList.isLoading,
      hasFetched: aguardandoRhList.hasFetched,
    },
    {
      key: "em_andamento", label: "Em andamento", dot: "blue.400",
      candidates: emAndamentoList.candidates,
      page: emAndamentoList.page, totalPages: emAndamentoList.totalPages,
      onPageChange: emAndamentoList.setPage, isLoading: emAndamentoList.isLoading,
      hasFetched: emAndamentoList.hasFetched,
    },
    {
      key: "concluidos", label: "Concluídos", dot: "green.400",
      candidates: concluidosList.candidates,
      page: concluidosList.page, totalPages: concluidosList.totalPages,
      onPageChange: concluidosList.setPage, isLoading: concluidosList.isLoading,
      hasFetched: concluidosList.hasFetched,
      headerExtra: (
        <WeekFilterChip
          active={concluidoWeekOnly}
          weekStart={weekRange.startDate}
          weekEnd={weekRange.endDate}
          onToggle={() => setConcluidoWeekOnly((v) => !v)}
          activeTitle="Mostrar todos os concluídos"
          inactiveTitle="Filtrar apenas os concluídos nesta semana"
        />
      ),
    },
    {
      key: "cancelados", label: "Cancelados", dot: "red.400",
      candidates: canceladosList.candidates,
      page: canceladosList.page, totalPages: canceladosList.totalPages,
      onPageChange: canceladosList.setPage, isLoading: canceladosList.isLoading,
      hasFetched: canceladosList.hasFetched,
      headerExtra: (
        <WeekFilterChip
          active={canceladoWeekOnly}
          weekStart={weekRange.startDate}
          weekEnd={weekRange.endDate}
          onToggle={() => setCanceladoWeekOnly((v) => !v)}
          label="Cancelado essa semana"
          activeTitle="Mostrar todos os cancelados"
          inactiveTitle="Filtrar apenas os cancelados nesta semana"
        />
      ),
    },
  ].filter((s) => hrFilters.status === "" || s.key === (HR_STATUS_TO_SECTION_KEY[hrFilters.status] ?? hrFilters.status));

  const tabs = [
    { label: "Processos RH", content: (
      <HrProcessesTab
        hrStats={hrStats} isLoadingHr={isLoadingHrStats} hrError={hrError}
        columns={hrColumns} sectionGroups={hrSectionGroups}
        viewMode={viewMode} onViewModeChange={setViewMode}
        onSectionExpand={handleHrSectionExpand}
        search={search} onSearchChange={setSearch}
        onExport={handleExport}
        onCartaProposta={handleExportCartaProposta}
        onViewDetail={(c) => setSelectedCandidate(c)}
        onCancel={handleCancelCandidate} onReactivate={handleReactivateCandidate}
        onRetry={retryHrSections}
        whitelistMap={hrWhitelistMap}
        selectedCandidateIds={selectedHrCandidateIds}
        onToggleSelect={handleHrToggleSelect}
        onSelectAll={handleHrSelectAll}
        candidateCount={viewMode === "kanban"
          ? aguardandoRhKanban.candidates.length + emAndamentoKanban.candidates.length + concluidosKanban.candidates.length + canceladosKanban.candidates.length
          : aguardandoRhList.candidates.length + emAndamentoList.candidates.length + concluidosList.candidates.length + canceladosList.candidates.length}
      />
    )},
    ...(hasHistory ? [{
      label: "Histórico", content: (
        <HrAuditLogPage
          token={token}
          refreshKey={auditRefreshKey}
          search={auditFilters.search}
          dateFrom={auditDateFrom}
          dateTo={auditDateTo}
          candidate={auditFilters.candidate}
        />
      ),
    }] : []),
  ];

  return (
    <Box display="flex" justifyContent="flex-start" alignItems="flex-start" minH="100vh">
      <Box display={{ base: "none", md: "flex" }}>
        {activeTab === 1 && hasHistory ? (
          <SideFilter<HrAuditSideFilters>
            filters={auditFilters}
            onChange={setAuditFilters}
            onReset={() => setAuditFilters({ ...DEFAULT_AUDIT_FILTERS })}
            fields={auditFilterFields}
            panelWidth={300}
          />
        ) : (
          <SideFilter<HrProcessSideFilters>
            filters={hrFilters}
            onChange={setHrFilters}
            onReset={() => setHrFilters({ ...DEFAULT_HR_FILTERS })}
            fields={hrFilterFields}
            panelWidth={300}
          />
        )}
      </Box>

      <Box flex="1" minW={0}>
        <Box maxW="container.xl" mx="auto" p={{ base: 4, md: 6 }}>
          <AdminTabSwitch index={activeTab} onChange={setActiveTab} tabs={tabs} />

          <CandidateDetailDrawer
            candidate={selectedCandidate}
            isOpen={!!selectedCandidate}
            onClose={() => setSelectedCandidate(null)}
            onRefresh={silentRefreshHr}
            onOptimisticPatch={patchHrWhitelistEntryOptimistic}
            mode="rh"
          />

          <AlertDialog
            isOpen={isCancelConfirmOpen}
            leastDestructiveRef={cancelConfirmRef}
            onClose={closeCancelConfirm}
            isCentered
          >
            <AlertDialogOverlay backdropFilter="blur(4px)" />
            <AlertDialogContent borderRadius="xl">
              <AlertDialogHeader fontSize="md" fontWeight={700}>
                Marcar como desistente
              </AlertDialogHeader>
              <AlertDialogBody fontSize="sm">
                Tem certeza que deseja marcar{" "}
                <strong>{pendingCancelCandidate?.name ?? pendingCancelCandidate?.email}</strong>{" "}
                como desistente? Essa ação pode ser revertida depois.
              </AlertDialogBody>
              <AlertDialogFooter gap={2}>
                <Button ref={cancelConfirmRef} variant="ghost" onClick={closeCancelConfirm}>
                  Voltar
                </Button>
                <Button colorScheme="red" onClick={handleConfirmCancel}>
                  Confirmar desistência
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Box>
      </Box>
    </Box>
  );
};

export default HrProcessesPage;